import React, { useState, useEffect } from 'react';
import { EntityType, IncidentType, Severity, User, Entity, UserRole, Taxi, Driver, Operator, Organization } from '../types';
import { createIncident, getOrganizations } from '../services/apiService';
import { LoadingSpinner } from './icons';

interface ReportIncidentPageProps {
  user: User;
  onReportSubmitted: () => void;
}

const ReportIncidentPage: React.FC<ReportIncidentPageProps> = ({ user, onReportSubmitted }) => {
  const [incidentType, setIncidentType] = useState<IncidentType>(IncidentType.GENERAL_INCIDENT);
  const [severity, setSeverity] = useState<Severity>(Severity.MINOR);
  const [description, setDescription] = useState('');
  const [debtAmount, setDebtAmount] = useState<number | undefined>();
  const [incidentDate, setIncidentDate] = useState(new Date().toISOString().split('T')[0]);

  const [entityType, setEntityType] = useState<EntityType>(EntityType.DRIVER);
  const [entityName, setEntityName] = useState('');
  const [entityIdField, setEntityIdField] = useState('');
  
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [reporterOrgId, setReporterOrgId] = useState<string>(user.organizationId);


  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const isPlatformAdmin = user.role === UserRole.SUPER_ADMIN || user.role === UserRole.PLATFORM_ADMIN;

  useEffect(() => {
    if (isPlatformAdmin) {
      const fetchAdminData = async () => {
        const orgs = await getOrganizations();
        setOrganizations(orgs);
      };
      fetchAdminData();
    }
  }, [isPlatformAdmin]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await createIncident({
        entityType,
        entityDetails: { name: entityName, idField: entityIdField },
        incidentType,
        severity,
        description,
        debtAmount,
        incidentDate,
        reporterOrgId: isPlatformAdmin ? reporterOrgId : user.organizationId,
      }, user);
      
      setSuccess('Incident reported successfully!');
      setDescription('');
      setDebtAmount(undefined);
      setEntityName('');
      setEntityIdField('');
      setTimeout(() => {
        onReportSubmitted();
      }, 1500);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };
  
  const getEntityIdLabel = () => {
      switch(entityType) {
          case EntityType.DRIVER: return "Licence Number";
          case EntityType.OPERATOR: return "ABN";
          case EntityType.TAXI: return "Plate Number";
      }
  }
  
  const getReporterOrgName = () => {
      if (isPlatformAdmin) {
          if (reporterOrgId === user.organizationId) return user.organizationName;
          return organizations.find(o => o.id === reporterOrgId)?.name || user.organizationName;
      }
      return user.organizationName;
  }

  return (
    <div className="bg-white p-4 sm:p-8 rounded-lg shadow-md max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-800 mb-2">Open a New Report</h1>
      <p className="text-gray-600 mb-6">Reported by: <span className="font-semibold">{getReporterOrgName()}</span></p>
      
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
                <h2 className="text-xl font-semibold border-b pb-2">Entity Details</h2>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Entity Type</label>
                    <select value={entityType} onChange={e => setEntityType(e.target.value as EntityType)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md">
                        {Object.values(EntityType).map(et => <option key={et} value={et}>{et}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">{entityType} Name</label>
                    <input type="text" value={entityName} onChange={e => setEntityName(e.target.value)} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md"/>
                </div>
                    <div>
                    <label className="block text-sm font-medium text-gray-700">{getEntityIdLabel()}</label>
                    <input type="text" value={entityIdField} onChange={e => setEntityIdField(e.target.value)} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md"/>
                </div>
            </div>

            <div className="space-y-6">
                <h2 className="text-xl font-semibold border-b pb-2">Incident Details</h2>
                 {isPlatformAdmin && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Report on Behalf of</label>
                        <select value={reporterOrgId} onChange={e => setReporterOrgId(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md">
                            <option value={user.organizationId}>{user.organizationName} (Myself)</option>
                            {organizations.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
                        </select>
                    </div>
                )}
                 <div>
                    <label className="block text-sm font-medium text-gray-700">Incident Type</label>
                    <select value={incidentType} onChange={e => setIncidentType(e.target.value as IncidentType)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md">
                         {Object.values(IncidentType).map(it => <option key={it} value={it}>{it}</option>)}
                    </select>
                </div>
                 <div>
                    <label className="block text-sm font-medium text-gray-700">Severity</label>
                    <select value={severity} onChange={e => setSeverity(e.target.value as Severity)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md">
                         {Object.values(Severity).map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                {incidentType === IncidentType.UNPAID_FEES_DEBT && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Debt Amount ($)</label>
                        <input type="number" value={debtAmount || ''} onChange={e => setDebtAmount(Number(e.target.value))} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md"/>
                    </div>
                )}
                <div>
                    <label className="block text-sm font-medium text-gray-700">Incident Date</label>
                    <input type="date" value={incidentDate} onChange={e => setIncidentDate(e.target.value)} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md"/>
                </div>
            </div>
        </div>

        <div className="mt-8 space-y-6">
            <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea value={description} onChange={e => setDescription(e.target.value)} required rows={5} className="mt-1 block w-full p-2 border border-gray-300 rounded-md"></textarea>
            </div>
            <div>
                 <label className="block text-sm font-medium text-gray-700">Evidence Upload (PDF, Image)</label>
                 <input type="file" multiple className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-primary hover:file:bg-blue-100"/>
            </div>
        </div>

        <div className="mt-8 border-t pt-6 flex justify-end">
            <button type="submit" disabled={isLoading} className="w-full md:w-auto flex justify-center py-3 px-6 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-primary hover:bg-primary-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-dark disabled:bg-gray-400">
                {isLoading ? <LoadingSpinner className="w-6 h-6"/> : 'Submit Report'}
            </button>
        </div>

        {error && <p className="mt-4 text-center text-red-600 bg-red-100 p-3 rounded-md">{error}</p>}
        {success && <p className="mt-4 text-center text-green-600 bg-green-100 p-3 rounded-md">{success}</p>}
      </form>
    </div>
  );
};

export default ReportIncidentPage;